import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {MethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import computeLSUVs from 'procedures/design/method-config/computeLSUVs';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {Method, ProcedureMaterialOutputSetting} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {STARTER_MATERIAL_OUTPUT_TYPE} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {transformLsuvsForRowFn} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import ProcedureMaterialOutputsTableAdapter, {
  ProcedureMaterialOutputsTableMetadata,
  ProcedureMaterialOutputsTableRowMetadata,
} from 'procedures/tables/ProcedureMaterialOutputsTableAdapter';
import {useAllTagSchemasWithStandardShape} from 'procedures/utils/useAllTagSchemas';

import ProcedureConfigTable from './ProcedureConfigTable';

const baseCreateMaterialOutputsTableProps: Omit<
  CreateStructuredTableProps<ProcedureMaterialOutputsTableMetadata>,
  'metadata'
> = {
  name: 'Material outputs',
  schemas: [],
  entryId: null,
  selectorsById: {},
  structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
};

interface MaterialOutputConfigTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  onReadyForEditing: () => void;
  locked: boolean;
  navigateToStep: (stepCid: string) => void;
}

export function makeEmptyMaterialOutputSetting(): ProcedureMaterialOutputSetting {
  return {
    materialOutputVersion: {
      cid: uuidv4(),
      id: null,
      procedureMaterialOutputId: null,
      name: '',
      description: '',
      dataType: STARTER_MATERIAL_OUTPUT_TYPE,
    },
  };
}

export default function MaterialOutputConfigTable(props: MaterialOutputConfigTableProps): JSX.Element {
  const {method, onChangeMethod, onReadyForEditing} = props;
  const {materialFlows} = ProcedurePageContext.useContext();
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const numberOfErrors = procedureValidationErrors.length;
  const adapter = ProcedureMaterialOutputsTableAdapter.getAdapter();
  const [tableRecreationTrigger, setTableRecreationTrigger] = useState(0);
  const tableCid = `procedureMaterialOutputsTable-${method.cid}`;
  const tagSchemas = useAllTagSchemasWithStandardShape(onReadyForEditing);
  const lsuvs = useRef<Record<string, Array<LastSyncedUpstreamValue>> | null>(null);
  const createMaterialOutputsTableProps = useMemo(
    () => ({
      ...baseCreateMaterialOutputsTableProps,
      metadata: {navigateToStep: props.navigateToStep},
    }),
    [props.navigateToStep]
  );

  const onChangeMaterialOutputSettingsDetails = useCallback(
    (materialOutputSettings: Array<ProcedureMaterialOutputSetting>) => {
      onChangeMethod(method.cid, (m) => ({...m, materialOutputSettings}));
      setTableRecreationTrigger((tr) => tr + 1);
    },
    [method, onChangeMethod]
  );

  // We need to rebuild the table whenever the error count changes, to ensure fresh cross-table error flags
  useEffect(() => {
    setTableRecreationTrigger((tr) => tr + 1);
  }, [numberOfErrors]);

  const materialOutputSettings =
    method.materialOutputSettings.length > 0
      ? method.materialOutputSettings
      : [makeEmptyMaterialOutputSetting()];

  const table = useMemo(
    () => {
      const t = adapter.createTableFromMaterials(
        createMaterialOutputsTableProps,
        tableCid,
        materialOutputSettings,
        materialFlows,
        procedureValidationErrors,
        method.parameterUsages
      );

      if (lsuvs.current == null) {
        lsuvs.current = computeLSUVs(
          t.rows,
          t.columns,
          method.materialOutputSettings,
          (mo) => mo.materialOutputVersion.id,
          transformLsuvsForRowFn(
            ProcedureMaterialOutputsTableAdapter.makeLastSyncedUpstreamValues,
            method.parameterUsages,
            (model) => model.materialOutputVersion.id
          )
        );
      }

      return {
        ...t,
        rows: t.rows.map((row, index) => {
          const outputSetting = materialOutputSettings[index];
          const id = outputSetting.materialOutputVersion.id;
          return {
            ...row,
            ...(id && lsuvs.current![id] ? {lastSyncedUpstreamValues: lsuvs.current![id]} : null),
            isFinalized: id != null,
          };
        }),
      };
    },
    // The table is recreated when "triggered" (via onChangeDetails, below) during an edit or when the
    // initial details have changed during a save
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableRecreationTrigger, method.materialOutputSettings]
  );

  return (
    <ProcedureConfigTable
      tableAdapter={ProcedureMaterialOutputsTableAdapter.getAdapter()}
      onChangeDetails={onChangeMaterialOutputSettingsDetails}
      onChangeErrorState={props.onChangeErrorState}
      rowToDetail={(
        row: StructuredTableRow<ProcedureMaterialOutputsTableRowMetadata>,
        columns: Array<StructuredTableColumn>
      ) => ProcedureMaterialOutputsTableAdapter.rowToMaterialOutputSetting(columns, row, tagSchemas ?? [])}
      table={table}
      tableCid={`procedureMaterialOutputsTable-${method.cid}`}
      locked={props.locked}
      dataTestAttrs={{component: 'MaterialOutputConfigTable', element: 'root'}}
    />
  );
}
